import { useEffect, useMemo, useState } from "react";
import { addTutorial, deleteTutorial, editTutorial, getTutorialByPageAdmin } from "../../HTTP/admin";
import { decodeHTML, encodeHTML } from "../../Helper/utils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const suggestedPages = {
    "/": "Accueil",
    "/login": "Connexion",
    "/resetpassword": "Reinitialisation du mot de passe",
    "/admin": "Administration",
    "/admin/options": "Options admin",
    "/admin/tutorials": "Tutoriels admin",
    "/admin/users": "Utilisateurs admin",
    "/admin/users/add": "Ajouter un utilisateur",
    "/admin/users/add-many": "Ajouter plusieurs utilisateurs",
    "/admin/users/edit/:id": "Modifier un utilisateur",
    "/admin/articles": "Articles admin",
    "/admin/articles/check/:id": "Verification article",
    "/admin/reviews": "Reviews admin",
    "/admin/reviews/check/:id": "Verification review",
    "/admin/subscription": "Abonnements admin",
    "/admin/subscription/add": "Ajouter un abonnement",
    "/admin/subscription/edit/:id": "Modifier un abonnement",
    "/admin/subscription/prices": "Tarifs abonnements",
    "/admin/subscription/prices/add": "Ajouter un tarif",
    "/admin/subscription/prices/edit/:id": "Modifier un tarif",
    "/admin/subscription/payments": "Paiements abonnements",
    "/admin/cse": "CSE admin",
    "/admin/cse/add": "Ajouter une CSE",
    "/admin/cse/edit/:id": "Modifier une CSE",
    "/admin/igo": "IGO admin",
    "/admin/igo/edit/:id": "Modifier une IGO",
    "/admin/igo/add": "Ajouter une IGO",
    "/admin/igo-theme": "Themes IGO",
    "/admin/igo-theme/add": "Ajouter un theme IGO",
    "/admin/igo-theme/edit/:id": "Modifier un theme IGO",
    "/admin/linksthematics": "Thematiques liens",
    "/admin/linksthematics/add": "Ajouter une thematique de liens",
    "/admin/linksthematics/edit/:id": "Modifier une thematique de liens",
    "/search": "Recherche",
    "/:countrycode/what-this-country-talk-about": "Ce dont ce pays parle",
    "/:countrycode/the-country-speaks-of": "Le pays parle de",
    "/:countrycode/which-countries-speak-about": "Quels pays parlent de",
    "/articles": "Articles",
    "/articles/:articleid": "Detail article",
    "/myarticles": "Mes articles",
    "/myarticles/create": "Creer un article",
    "/myarticles/edit/:id": "Modifier un article",
    "/notifications": "Notifications",
    "/feed": "Fil d'actualite",
    "/favourites": "Favoris",
    "/friends": "Amis",
    "/friends/:id": "Profil ami",
    "/followed": "Suivis",
    "/editkeywords/:id": "Modifier mots-cles",
    "/addkeywords": "Ajouter mots-cles",
    "/friends-msg": "Messages amis",
    "/profile": "Profil",
    "/profile/subscription": "Abonnement profil",
    "/edit": "Modifier profil",
    "/Subscription": "Abonnement",
    "/addsubscription": "Ajouter abonnement",
    "/nextpagepayment": "Page suivante paiement",
    "/subscription/beneficiary": "Beneficiaire abonnement",
    "/map": "Carte",
    "/mypublications": "Mes publications",
    "/reviews/:reviewid": "Detail review",
    "/reviews/:reviewid/addfavourites": "Ajouter review aux favoris",
    "/addtoreview": "Ajouter a review",
    "/addtofriendreview": "Ajouter a la review d'un ami",
    "/editreviews/:reviewid": "Modifier review",
    "/igo": "IGO",
    "/zemus-ia": "Zemus IA",
    "/conditions": "Conditions",
    "/legales": "Mentions legales",
    "/politique": "Politique",
    "/about": "A propos",
};

function extractContent(tutorial) {
    if (!tutorial) return "";
    return tutorial.content ?? tutorial.context ?? "";
}

export default function TutorialAdmin() {
    const localUser = JSON.parse(localStorage.getItem("user"));
    if (!localUser.super_admin && localUser.admin_access !== 7) {
        window.location.href = "/";
    }

    const [selectedPage, setSelectedPage] = useState("/feed");
    const [pageInput, setPageInput] = useState("/feed");
    const [content, setContent] = useState("");
    const [currentTutorial, setCurrentTutorial] = useState(null);
    const [loading, setLoading] = useState(false);

    const pageChoices = useMemo(() => Object.entries(suggestedPages), []);

    const loadPageTutorial = async (targetPage) => {
        const normalizedPage = (targetPage || "").trim();
        if (!normalizedPage) {
            alert("Veuillez renseigner une page.");
            return;
        }
        setLoading(true);
        try {
            const tutorial = await getTutorialByPageAdmin(normalizedPage);
            setCurrentTutorial(tutorial || null);
            setContent(tutorial ? decodeHTML(extractContent(tutorial)) : "");
        } catch (error) {
            console.error("Erreur de chargement du tutoriel:", error);
            setCurrentTutorial(null);
            setContent("");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPageTutorial(selectedPage);
    }, [selectedPage]);

    const savePayload = () => ({
        page: selectedPage,
        content: encodeHTML(content || "")
    });

    const handleAdd = async () => {
        if (!selectedPage || !content.trim()) {
            alert("La page et le contenu sont obligatoires.");
            return;
        }
        const response = await addTutorial(savePayload());
        if (response) {
            alert("Tutoriel ajouté.");
            await loadPageTutorial(selectedPage);
        } else {
            alert("Erreur pendant l'ajout.");
        }
    };

    const handleEdit = async () => {
        if (!selectedPage || !content.trim()) {
            alert("La page et le contenu sont obligatoires.");
            return;
        }
        const response = await editTutorial(savePayload());
        if (response) {
            alert("Tutoriel modifié.");
            await loadPageTutorial(selectedPage);
        } else {
            alert("Erreur pendant la modification.");
        }
    };

    const handleDelete = async () => {
        if (!currentTutorial?.id) {
            alert("Aucun tutoriel à supprimer pour cette page.");
            return;
        }
        if (!window.confirm("Supprimer ce tutoriel ?")) {
            return;
        }
        const response = await deleteTutorial(currentTutorial.id);
        if (response) {
            alert("Tutoriel supprimé.");
            setCurrentTutorial(null);
            setContent("");
        } else {
            alert("Erreur pendant la suppression.");
        }
    };

    const handleApplyManualPage = () => {
        const normalized = (pageInput || "").trim();
        if (!normalized.startsWith("/")) {
            alert("La page doit commencer par '/'.");
            return;
        }
        setSelectedPage(normalized);
    };

    return (
        <div className="flex flex-row mt-8">
            <div className="flex-grow px-4 sm:px-6 lg:px-8">
                <h1 className="text-2xl font-bold text-center mb-6">Gestion des tutoriels</h1>

                <div className="bg-white rounded-lg shadow-md p-4 mb-4">
                    <label className="block text-sm font-bold mb-2">Page (URL)</label>
                    <div className="flex flex-col lg:flex-row gap-2 mb-3 notranslate">
                        <select
                            className="w-full lg:w-1/2 px-3 py-2 border border-gray-300 rounded-md"
                            value={pageChoices.some(([page]) => page === selectedPage) ? selectedPage : ""}
                            onChange={(e) => {
                                if (e.target.value) {
                                    setPageInput(e.target.value);
                                    setSelectedPage(e.target.value);
                                }
                            }}
                        >
                            <option value="">Choisir une page suggérée</option>
                            {pageChoices.map(([page, label]) => (
                                <option key={page} value={page}>{label}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            className="w-full lg:w-1/2 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="/ma-page"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleApplyManualPage}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Charger la page
                        </button>
                        <button
                            type="button"
                            onClick={() => loadPageTutorial(selectedPage)}
                            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Recharger
                        </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-3">
                        Page active: <span className="font-semibold">{selectedPage}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                        Statut: {loading ? "Chargement..." : currentTutorial ? `Tutoriel trouvé (id: ${currentTutorial.id})` : "Aucun tutoriel"}
                    </p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-4">
                    <label className="block text-sm font-bold mb-2">Contenu HTML (non échappé)</label>
                    <div className="mb-16 notranslate">
                        <ReactQuill
                            value={content}
                            onChange={setContent}
                            placeholder="Écrivez le tutoriel ici..."
                            theme="snow"
                            style={{ height: "280px" }}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                        <button
                            type="button"
                            onClick={handleAdd}
                            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Ajouter
                        </button>
                        <button
                            type="button"
                            onClick={handleEdit}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Modifier
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                        >
                            Supprimer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
